export type ZoomAnchor = {
  clientX: number;
  clientY: number;
  oldZoom: number;
  newZoom: number;
  /** Scroll offsets when the zoom step started (for idempotent correction). */
  scrollLeft0: number;
  scrollTop0: number;
  /** Viewport rects at anchor time (stable across post-layout rAF passes). */
  scrollerLeft: number;
  scrollerTop: number;
  layerLeft: number;
  layerTop: number;
};

/** Trackpad pinch sensitivity; lower = smoother steps. */
export const WHEEL_ZOOM_SENSITIVITY = 0.0045;

/** Minimum relative zoom change per wheel step (avoids no-op at 100%). */
export const MIN_ZOOM_STEP_RATIO = 1.0001;

/** Epsilon for comparing live zoom to fit (1.0). */
export const ZOOM_FIT_EPSILON = 1e-9;

/** Layout mode: compact fit chrome at or below 100%, pan layout above. */
export function isZoomedLayout(zoom: number, fitMode = false): boolean {
  return !fitMode && zoom > 1 + ZOOM_FIT_EPSILON;
}

export function isLiveZoomPastFit(liveZoom: number): boolean {
  return Math.abs(liveZoom - 1) > ZOOM_FIT_EPSILON;
}

export function normalizeWheelDelta(delta: number, deltaMode = 0): number {
  if (deltaMode === 1) return delta * 16;
  if (deltaMode === 2) return delta * 400;
  return delta;
}

export function wheelZoomFactor(deltaY: number, deltaMode = 0): number {
  const delta = normalizeWheelDelta(deltaY, deltaMode);
  return Math.exp(-delta * WHEEL_ZOOM_SENSITIVITY);
}

export type PinchWheelLike = Pick<WheelEvent, "ctrlKey" | "metaKey" | "deltaY" | "deltaX" | "deltaMode"> & {
  getModifierState?: (key: string) => boolean;
};

/** True for trackpad pinch (Chrome/Edge/Safari send ctrl+wheel; Safari may use meta). */
export function isPinchZoomWheel(e: PinchWheelLike): boolean {
  if (e.ctrlKey || e.getModifierState?.("Control")) return true;
  const dy = Math.abs(normalizeWheelDelta(e.deltaY, e.deltaMode));
  const dx = Math.abs(normalizeWheelDelta(e.deltaX ?? 0, e.deltaMode));
  const pinchDelta = Math.max(dy, dx);
  if (pinchDelta <= 0) return false;
  // macOS Safari sometimes reports pinch as meta+small wheel delta.
  if (e.metaKey && pinchDelta < 120) return true;
  return false;
}

/** Accept a zoom step if it moves meaningfully (tiny deltas at 100% still count). */
export function shouldAcceptZoomStep(oldZoom: number, newZoom: number): boolean {
  if (oldZoom <= 0 || newZoom <= 0) return false;
  const ratio = newZoom / oldZoom;
  return ratio >= MIN_ZOOM_STEP_RATIO || ratio <= 1 / MIN_ZOOM_STEP_RATIO;
}

/**
 * Scroll target that keeps the point under the cursor fixed.
 * Uses zoom-layer origin so unscaled chrome (page nav, margins) does not skew the anchor.
 */
export function computeZoomScrollTarget(anchor: ZoomAnchor): { scrollLeft: number; scrollTop: number } {
  const offsetX = anchor.clientX - anchor.scrollerLeft;
  const offsetY = anchor.clientY - anchor.scrollerTop;
  const layerOriginX = anchor.scrollLeft0 + (anchor.layerLeft - anchor.scrollerLeft);
  const layerOriginY = anchor.scrollTop0 + (anchor.layerTop - anchor.scrollerTop);
  const localX = (anchor.clientX - anchor.layerLeft) / anchor.oldZoom;
  const localY = (anchor.clientY - anchor.layerTop) / anchor.oldZoom;
  return {
    scrollLeft: layerOriginX + localX * anchor.newZoom - offsetX,
    scrollTop: layerOriginY + localY * anchor.newZoom - offsetY,
  };
}

/** Keep the content point under the cursor fixed while zoom changes. */
export function scrollForZoomAtPoint(scroller: HTMLElement, anchor: ZoomAnchor): void {
  const target = computeZoomScrollTarget(anchor);
  scroller.scrollLeft = target.scrollLeft;
  scroller.scrollTop = target.scrollTop;
}

/** Re-apply scroll anchor after layout reflow (overflow appears past 100%). */
export function scrollForZoomAtPointAfterLayout(
  wrap: HTMLElement,
  anchor: ZoomAnchor,
  scheduleFrame: (cb: () => void) => number = (cb) => requestAnimationFrame(cb),
): void {
  const scroller = resolvePreviewZoomScroller(wrap);
  scrollForZoomAtPoint(scroller, anchor);
  scheduleFrame(() => {
    scrollForZoomAtPoint(scroller, anchor);
    scheduleFrame(() => scrollForZoomAtPoint(scroller, anchor));
  });
}

function scrollAfterLayoutFlip(
  wrap: HTMLElement,
  layer: HTMLElement,
  anchor: ZoomAnchor,
  zoom: number,
  scheduleFrame: (cb: () => void) => number = (cb) => requestAnimationFrame(cb),
): void {
  scheduleFrame(() => {
    const fresh = buildZoomAnchor(wrap, layer, anchor.clientX, anchor.clientY, anchor.oldZoom, zoom);
    const scroller = resolvePreviewZoomScroller(wrap);
    scrollForZoomAtPoint(scroller, fresh);
    scheduleFrame(() => scrollForZoomAtPoint(scroller, fresh));
  });
}

function isWrapFullscreen(wrap: HTMLElement): boolean {
  if (typeof document === "undefined") return false;
  const doc = document as Document & { webkitFullscreenElement?: Element | null };
  const fs = doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
  return fs === wrap;
}

function resolveDocumentScroller(wrap: HTMLElement): HTMLElement | null {
  return wrap.querySelector<HTMLElement>(".document-viewer, .pdf-viewer, .image-preview__viewport");
}

function getVerticalScroller(start: HTMLElement): HTMLElement {
  let node: HTMLElement | null = start.parentElement;
  while (node && node !== document.documentElement) {
    const style = getComputedStyle(node);
    const oy = style.overflowY;
    const ox = style.overflowX;
    const scrollableY =
      (oy === "auto" || oy === "scroll" || oy === "overlay") && node.scrollHeight > node.clientHeight + 1;
    const scrollableX =
      (ox === "auto" || ox === "scroll" || ox === "overlay") && node.scrollWidth > node.clientWidth + 1;
    if (scrollableY || scrollableX) return node;
    node = node.parentElement;
  }
  return (document.scrollingElement as HTMLElement) || document.documentElement;
}

/** Scroll container that should be adjusted when pinch/wheel zoom runs. */
export function resolvePreviewZoomScroller(wrap: HTMLElement): HTMLElement {
  const docScroller = resolveDocumentScroller(wrap);
  if (docScroller) return docScroller;
  if (isWrapFullscreen(wrap)) return wrap;
  const main = wrap.closest<HTMLElement>(".preview-standalone__main");
  if (main) return main;
  const drawer = wrap.closest<HTMLElement>(".preview-drawer__panel");
  if (drawer) return drawer;
  return getVerticalScroller(wrap);
}

export type ApplyPreviewZoomOptions = {
  anchor?: ZoomAnchor | null;
  /** Clear CSS zoom and layout mode (Fit). */
  fitMode?: boolean;
};

/** True when user has pinched past fit (guards resize-driven PDF reload). */
export function isPreviewZoomActive(
  zoomScale: number | null,
  liveZoom: number,
  pinchActive = false,
): boolean {
  return pinchActive || zoomScale != null || isLiveZoomPastFit(liveZoom);
}

function setZoomedLayoutClasses(wrap: HTMLElement, on: boolean): void {
  wrap.classList.toggle("document-preview-wrap--zoomed", on);
  wrap.classList.toggle("pdf-preview-wrap--zoomed", on);
}

function syncZoomLayerLayout(layer: HTMLElement, fitMode: boolean): void {
  if (fitMode) {
    layer.style.width = "";
    layer.style.minWidth = "";
    return;
  }
  layer.style.width = "max-content";
  layer.style.minWidth = "100%";
}

/** Apply zoom to the preview layer synchronously (avoids React frame lag). */
export function applyPreviewZoom(
  wrap: HTMLElement,
  layer: HTMLElement,
  zoom: number,
  options: ApplyPreviewZoomOptions = {},
): void {
  const { anchor, fitMode = false } = options;
  const wasZoomed = wrap.classList.contains("document-preview-wrap--zoomed");
  const panLayout = isZoomedLayout(zoom, fitMode);

  if (fitMode) {
    layer.style.zoom = "";
    syncZoomLayerLayout(layer, true);
    setZoomedLayoutClasses(wrap, false);
  } else if (!panLayout) {
    // Sub-100% pinch: scale inside compact strip without pan layout.
    layer.style.zoom = String(zoom);
    syncZoomLayerLayout(layer, true);
    setZoomedLayoutClasses(wrap, false);
  } else {
    layer.style.zoom = String(zoom);
    syncZoomLayerLayout(layer, false);
    setZoomedLayoutClasses(wrap, true);
  }

  if (anchor) {
    if (wasZoomed !== panLayout) {
      scrollAfterLayoutFlip(wrap, layer, anchor, zoom);
    } else {
      scrollForZoomAtPointAfterLayout(wrap, anchor);
    }
  }
}

/** Build anchor with scroll and layer geometry snapshot for a pinch step. */
export function buildZoomAnchor(
  wrap: HTMLElement,
  layer: HTMLElement,
  clientX: number,
  clientY: number,
  oldZoom: number,
  newZoom: number,
): ZoomAnchor {
  const scroller = resolvePreviewZoomScroller(wrap);
  const scrollerRect = scroller.getBoundingClientRect();
  const layerRect = layer.getBoundingClientRect();
  return {
    clientX,
    clientY,
    oldZoom,
    newZoom,
    scrollLeft0: scroller.scrollLeft,
    scrollTop0: scroller.scrollTop,
    scrollerLeft: scrollerRect.left,
    scrollerTop: scrollerRect.top,
    layerLeft: layerRect.left,
    layerTop: layerRect.top,
  };
}
